import { Card, CardContent } from "@/components/ui/card";
import {
  TRANSFER_STAT_CARD,
  TRANSFER_STAT_CARD_BODY,
  TRANSFER_STAT_GRID,
} from "@/modules/inventory/presentation/ui/transfers/transferCardLayout";
import type { PurchaseOrderReportSummary } from "@/modules/reports/domain/repo/purchase-order/get-purchase-order-report-summary";
import { formatLakDisplay, formatQtyDisplay } from "@/shared/lib/format-number";
import { ClipboardListIcon, ClockIcon, PackageIcon, WalletIcon } from "lucide-react";

type Props = {
  summary?: PurchaseOrderReportSummary;
  isLoading?: boolean;
};

export function PurchaseOrderReportsSummaryCards({ summary, isLoading }: Props) {
  if (isLoading) {
    return (
      <div className={TRANSFER_STAT_GRID}>
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className={TRANSFER_STAT_CARD}>
            <CardContent className={TRANSFER_STAT_CARD_BODY}>
              <div className="h-10 w-full animate-pulse rounded bg-muted" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const s = summary;

  return (
    <div className={TRANSFER_STAT_GRID}>
      <Card className={`${TRANSFER_STAT_CARD} border-amber-500/20`}>
        <CardContent className={TRANSFER_STAT_CARD_BODY}>
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-amber-500/10">
            <ClipboardListIcon className="size-5 text-amber-600" />
          </div>
          <div className="min-w-0">
            <p className="text-muted-foreground text-sm">PO ຄ້າງຮັບ</p>
            <p className="font-semibold text-2xl tabular-nums">
              {(s?.openReceivablePoCount ?? 0).toLocaleString("lo-LA")}
            </p>
            <p className="text-muted-foreground text-xs">
              {formatQtyDisplay(s?.qtyOpen ?? 0)} ຫນ່ວຍຄ້າງ
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className={TRANSFER_STAT_CARD}>
        <CardContent className={TRANSFER_STAT_CARD_BODY}>
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <WalletIcon className="size-5 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-muted-foreground text-sm">ມູນຄ່າຄ້າງຮັບ</p>
            <p className="font-semibold text-2xl tabular-nums">
              {formatLakDisplay(s?.valuePendingLak ?? 0)}
            </p>
            <p className="text-muted-foreground text-xs">
              {formatQtyDisplay(s?.openLineCount ?? 0)} ບັນທັດ
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className={TRANSFER_STAT_CARD}>
        <CardContent className={TRANSFER_STAT_CARD_BODY}>
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-blue-500/10">
            <ClockIcon className="size-5 text-blue-600" />
          </div>
          <div className="min-w-0">
            <p className="text-muted-foreground text-sm">ລໍຖ້າອະນຸມັດ</p>
            <p className="font-semibold text-2xl tabular-nums">
              {(s?.submittedPendingCount ?? 0).toLocaleString("lo-LA")}
            </p>
            <p className="text-muted-foreground text-xs">ສະຖານະ SUBMITTED</p>
          </div>
        </CardContent>
      </Card>

      <Card className={TRANSFER_STAT_CARD}>
        <CardContent className={TRANSFER_STAT_CARD_BODY}>
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted">
            <PackageIcon className="size-5 text-muted-foreground" />
          </div>
          <div className="min-w-0">
            <p className="text-muted-foreground text-sm">ຮັບແລ້ວ / ສັ່ງ (ບັນທັດຄ້າງ)</p>
            <p className="font-semibold text-lg tabular-nums">
              {formatQtyDisplay(s?.qtyReceivedOnOpenLines ?? 0)} /{" "}
              {formatQtyDisplay(s?.qtyOrderedOnOpenLines ?? 0)}
            </p>
            <p className="text-muted-foreground text-xs">ຕາມຕົວກອງວັນທີສັ່ງ</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
